import React from 'react';

export interface SidebarRailProps {
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  availableCategories: string[];
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  matchPattern?: RegExp;
}

export const SidebarRail: React.FC<SidebarRailProps> = ({
  selectedCategory,
  onSelectCategory,
  availableCategories,
}) => {
  const navItems: NavItem[] = [
    {
      id: 'All',
      label: 'Explore',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" strokeWidth="2" strokeLinejoin="round" fill="currentColor" fillOpacity="0.2" />
        </svg>
      ),
    },
    {
      id: 'Food',
      label: 'Food',
      matchPattern: /food|catering|tiffin|restaurant|sweet|snack/i,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 10a6 6 0 00-12 0v8h12v-8z" />
        </svg>
      ),
    },
    {
      id: 'Business',
      label: 'Business',
      matchPattern: /business|retail|textile|trading|shop/i,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: 'Services',
      label: 'Services',
      matchPattern: /service|professional|consultant|agency/i,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <circle cx="12" cy="12" r="3" strokeWidth="2" />
        </svg>
      ),
    },
    {
      id: 'Healthcare',
      label: 'Healthcare',
      matchPattern: /health|doctor|clinic|medical/i,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    },
    {
      id: 'Education',
      label: 'Education',
      matchPattern: /education|tuition|school|class|coaching/i,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      ),
    },
    {
      id: 'Community',
      label: 'Community',
      matchPattern: /community|samaj|mandal|trust|ngo/i,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
  ];

  const handleItemClick = (item: NavItem) => {
    if (item.id === 'All') {
      onSelectCategory('All');
      return;
    }

    // Try finding exact match or pattern match in available categories
    if (item.matchPattern) {
      const matched = availableCategories.find(cat => item.matchPattern!.test(cat));
      if (matched) {
        onSelectCategory(matched);
        return;
      }
    }

    // Fallback: set the category directly
    onSelectCategory(item.id);
  };

  const isItemActive = (item: NavItem) => {
    if (item.id === 'All') {
      return selectedCategory === 'All';
    }
    if (selectedCategory.toLowerCase() === item.id.toLowerCase()) {
      return true;
    }
    if (item.matchPattern && item.matchPattern.test(selectedCategory)) {
      return true;
    }
    return false;
  };

  return (
    <aside className="w-24 lg:w-28 flex-shrink-0 flex flex-col gap-6">
      {/* Navigation Icons Container */}
      <nav aria-label="Directory Categories" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-3 shadow-soft border border-orange-100/60 dark:border-gray-700/60 flex flex-col items-center gap-2">
        {navItems.map((item) => {
          const active = isItemActive(item);
          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              aria-label={item.label}
              className={`w-full py-3 px-1 rounded-2xl flex flex-col items-center justify-center transition-all duration-200 group ${
                active
                  ? 'bg-orange-100/90 dark:bg-orange-950/50 text-orange-600 dark:text-orange-400 font-bold shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-300 hover:bg-orange-50/60 dark:hover:bg-gray-700/40'
              }`}
            >
              <div
                className={`p-2 rounded-xl transition-transform duration-200 group-hover:scale-110 ${
                  active ? 'bg-orange-500 text-white shadow-sm' : ''
                }`}
              >
                {item.icon}
              </div>
              <span className="text-[11px] font-medium tracking-tight mt-1 text-center">
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Community Banner Card */}
      <div className="bg-gradient-to-b from-[#FFF2E2] to-[#FFE3CB] dark:from-orange-950/40 dark:to-orange-900/20 rounded-3xl p-4 border border-orange-200/60 dark:border-orange-800/40 flex flex-col items-center text-center shadow-soft relative overflow-hidden">
        <p className="font-handwriting text-lg leading-tight font-bold text-orange-700 dark:text-orange-300">
          Stronger<br />Together<br />
          <span className="text-sm font-normal text-orange-600/90 dark:text-orange-400/90">in a New City</span>
        </p>
        <span className="text-orange-500 text-base mt-1">🧡</span>

        {/* Subtle decorative leaf/botanical flourish */}
        <div className="mt-3 opacity-70 text-emerald-700 dark:text-emerald-400">
          <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66C7.6 17.5 9.5 12 17 10V8zm0 0c2 2 3 5 2 8-1 3-3 5-3 5s1-3 0-5c-1-2-2-4 1-8z" />
          </svg>
        </div>
      </div>
    </aside>
  );
};

export default SidebarRail;
